/**
 * O catálogo de permissões em português, para quem escolhe o papel de alguém.
 *
 * O servidor conhece as permissões por slug (`participants.read_pii`) e é ele
 * quem manda: esta lista só traduz. Por isso a tela mostra o que o `/roles`
 * devolveu — se um slug aparecer aqui e não existir mais lá, ele simplesmente
 * não é exibido, em vez de virar uma linha mentindo que a permissão existe.
 *
 * A ordem dos grupos segue a jornada do trabalho: primeiro o evento, depois as
 * pessoas dele, depois a conversa, e por último a administração.
 */
export interface Permissao {
  slug: string;
  rotulo: string;
  /** Só quando a consequência não é óbvia pelo rótulo. */
  detalhe?: string;
}

export interface GrupoDePermissoes {
  titulo: string;
  icone: string;
  permissoes: Permissao[];
}

/**
 * Uma frase para cada papel de sistema, dizendo o que ele é.
 *
 * O `Role` tem um campo `description` no banco, mas o seed não preenche — e uma
 * coluna de "sem descrição" repetida cinco vezes não ajuda ninguém a escolher.
 * Quando o campo for preenchido, o que vem do servidor vence: isto é o que
 * aparece na falta dele.
 *
 * ⚠️ Descreve o PADRÃO. As permissões de um papel podem ser editadas por
 * organização — a tabela ao lado é que diz a verdade do momento.
 */
export const DESCRICAO_PADRAO_DO_PAPEL: Record<string, string> = {
  'admin-organizacao':
    'Manda em tudo dentro do cliente: equipe, configurações, integrações, IA e auditoria.',
  'gestor-evento':
    'Toca o evento de ponta a ponta — cria, importa a lista, dispara as jornadas e aprova conexões. Não mexe na equipe nem nas configurações.',
  operador:
    'O dia a dia: cuida da lista, inicia e agenda jornadas, acompanha as conversas e assume quando precisa. Não cria evento.',
  analista:
    'Só leitura, com números: painéis, relatórios para exportar, conversas e recomendações. Não dispara nada e não vê contato sem máscara.',
  visualizador: 'Acompanha o evento, a lista e os painéis. Não altera nada.',
};

export const GRUPOS_DE_PERMISSOES: GrupoDePermissoes[] = [
  {
    titulo: 'Eventos',
    icone: 'ti-calendar-event',
    permissoes: [
      { slug: 'events.read', rotulo: 'Ver eventos' },
      { slug: 'events.create', rotulo: 'Criar eventos' },
      { slug: 'events.update', rotulo: 'Editar eventos' },
      { slug: 'events.delete', rotulo: 'Excluir eventos' },
    ],
  },
  {
    titulo: 'Participantes',
    icone: 'ti-users',
    permissoes: [
      { slug: 'participants.read', rotulo: 'Ver a lista de participantes' },
      {
        slug: 'participants.read_pii',
        rotulo: 'Ver telefone e e-mail sem máscara',
        detalhe:
          'Separada das demais por exigência da LGPD: dá para trabalhar com a lista inteira sem enxergar o contato de ninguém.',
      },
      { slug: 'participants.create', rotulo: 'Cadastrar participante' },
      { slug: 'participants.update', rotulo: 'Editar participante' },
      { slug: 'participants.import', rotulo: 'Importar planilha' },
      { slug: 'participants.delete', rotulo: 'Excluir participante' },
    ],
  },
  {
    titulo: 'Jornadas',
    icone: 'ti-route',
    permissoes: [
      {
        slug: 'journeys.start',
        rotulo: 'Iniciar jornada',
        detalhe: 'É o que faz a IA começar a falar com os participantes no WhatsApp.',
      },
      { slug: 'journeys.schedule', rotulo: 'Agendar início' },
      { slug: 'journeys.pause', rotulo: 'Pausar jornada' },
      { slug: 'journeys.cancel', rotulo: 'Cancelar jornada' },
    ],
  },
  {
    titulo: 'Conversas',
    icone: 'ti-message-circle',
    permissoes: [
      { slug: 'conversations.read', rotulo: 'Ler as conversas' },
      {
        slug: 'conversations.takeover',
        rotulo: 'Assumir a conversa',
        detalhe: 'Tira a IA do volante e põe uma pessoa respondendo no lugar dela.',
      },
      { slug: 'conversations.send', rotulo: 'Enviar mensagem' },
    ],
  },
  {
    titulo: 'Mensagens aprovadas',
    icone: 'ti-template',
    permissoes: [
      { slug: 'templates.manage', rotulo: 'Criar e editar templates' },
      {
        slug: 'templates.submit',
        rotulo: 'Enviar template para a Meta',
        detalhe: 'Quem submete responde pelo que vai para aprovação em nome da conta.',
      },
    ],
  },
  {
    titulo: 'Conexões',
    icone: 'ti-arrows-shuffle',
    permissoes: [
      { slug: 'matching.rules_manage', rotulo: 'Definir as regras de conexão' },
      { slug: 'recommendations.read', rotulo: 'Ver as recomendações' },
      { slug: 'recommendations.approve', rotulo: 'Aprovar recomendações' },
      {
        slug: 'recommendations.send',
        rotulo: 'Enviar a apresentação',
        detalhe: 'É o passo que chega ao participante — os dois lados precisam ter aceitado.',
      },
    ],
  },
  {
    titulo: 'Painéis e relatórios',
    icone: 'ti-chart-bar',
    permissoes: [
      { slug: 'dashboards.read', rotulo: 'Ver os painéis' },
      { slug: 'reports.export', rotulo: 'Exportar relatórios' },
    ],
  },
  {
    titulo: 'Administração',
    icone: 'ti-settings',
    permissoes: [
      {
        slug: 'users.manage',
        rotulo: 'Gerenciar usuários e papéis',
        detalhe: 'Quem tem isto pode dar a outra pessoa qualquer permissão desta lista.',
      },
      { slug: 'settings.manage', rotulo: 'Configurações da organização' },
      { slug: 'ai.settings_manage', rotulo: 'Ajustes da IA' },
      { slug: 'integrations.manage', rotulo: 'Integrações (WhatsApp e afins)' },
      { slug: 'audit.read', rotulo: 'Ler o registro de auditoria' },
    ],
  },
];
